import { RegionShared } from "@1games-ru/region-shared";
import {
  VehicleChargeGrade,
  VehicleFuelGrade,
  VehicleFuelType,
} from "@1games-ru/region-shared/dist/enums/vehicle/vehicle";
import { isNull } from "lodash";
import { useCallback, useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import rpc from "@1games-ru/rage-rpc";
import VehicleCdn from "@/core/cdn/vehicle/vehicle-cdn.ts";
import GasStationMpProvider from "@/core/data-provider/data-mp/gas-station/gas-station-mp-provider.ts";
import VehicleMpProvider from "@/core/data-provider/data-mp/vehicle/vehicle-mp-provider.ts";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector.ts";
import { RpcMpProviderFailedResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-failed-response.ts";
import {
  refuelingTimeSelector,
  reset,
  setSelectedFuelGrade,
  setSelectedFuelType,
  setSelectedProductId,
  setStation,
  setVehicle,
} from "@/domains/gasStation/slice";
import toast from "@/domains/toasts/helpers/toast.ts";

export default () => {
  const { t, i18n } = useTranslation("gasStation");
  const [isShow, setIsShow] = useState(false);
  const refuelingTime = useAppSelector(refuelingTimeSelector);
  const dispatch = useAppDispatch();

  const close = useCallback(() => {
    if (refuelingTime) {
      return;
    }
    setIsShow(false);
    dispatch(reset());
  }, [dispatch, refuelingTime]);

  useLayoutEffect(() => {
    rpc.register(
      RegionShared.Cef.Events.GasStation.GasStationShow,
      async (request: typeof RegionShared.Cef.Requests.GasStationShowRequest): Promise<void> => {
        if (isShow) {
          close();
          return;
        }

        const { vehicleMpId, gasStationId } = request;

        const gasStationResponse = await GasStationMpProvider.getGasStation(gasStationId);

        if (gasStationResponse instanceof RpcMpProviderFailedResponse) {
          console.error(gasStationResponse.error.message);
          if (i18n.exists(`gasStation:gasStation.errors.${gasStationResponse.error.message}`)) {
            toast.error(t(`gasStation.errors.${gasStationResponse.error.message}`));
          } else {
            toast.error(t("gasStation.toast.gasStationLoadError"));
          }
          return;
        }

        const vehicleResponse = await VehicleMpProvider.getVehicleFuel(vehicleMpId);

        if (vehicleResponse instanceof RpcMpProviderFailedResponse) {
          console.error(vehicleResponse.error.message);
          if (i18n.exists(`gasStation:gasStation.errors.${vehicleResponse.error.message}`)) {
            toast.error(t(`gasStation.errors.${vehicleResponse.error.message}`));
          } else {
            toast.error(t("gasStation.toast.vehicleLoadError"));
          }
          return;
        }

        dispatch(
          setStation({
            id: gasStationResponse.data.gasStationId,
            name: gasStationResponse.data.name,
            address: {
              index: gasStationResponse.data.address.index,
              streetName: gasStationResponse.data.address.streetName,
            },
            brand: gasStationResponse.data.brand,
            fuelTypes: gasStationResponse.data.fuelTypes,
          }),
        );
        dispatch(
          setVehicle({
            brand: vehicleResponse.data.name.brandName,
            fuelAmount: vehicleResponse.data.fuelAmount,
            fuelMaxAmount: vehicleResponse.data.fuelMaxAmount,
            fuelType: vehicleResponse.data.fuelType,
            id: vehicleResponse.data.vehicleMpId,
            model: vehicleResponse.data.name.modelName,
            brandImage: VehicleCdn.getVehicleBrandImage(vehicleResponse.data.name.brand),
            modelImage: VehicleCdn.getVehicleImage(vehicleResponse.data.model),
          }),
        );

        const fuelTypes = Object.entries(gasStationResponse.data.fuelTypes);
        const currentFuelType =
          fuelTypes.find(
            ([key, value]) => key === vehicleResponse.data.fuelType && !isNull(value),
          )?.[1] || null;
        dispatch(setSelectedFuelType(vehicleResponse.data.fuelType));

        let grade = null;

        if (vehicleResponse.data.fuelType === VehicleFuelType.Gas) {
          grade = VehicleFuelGrade.Good;
        }

        if (vehicleResponse.data.fuelType === VehicleFuelType.Electric) {
          grade = VehicleChargeGrade.Standard;
        }

        if (currentFuelType) {
          if ("id" in currentFuelType) {
            dispatch(setSelectedProductId(currentFuelType.id));
          } else if (grade) {
            dispatch(setSelectedProductId(currentFuelType[grade].id));
          } else {
            const firstItem = Object.values(currentFuelType)[0];
            if ("id" in firstItem) {
              dispatch(setSelectedProductId(firstItem.id));
            }
          }
        }

        dispatch(setSelectedFuelGrade(grade));
        setIsShow(true);
      },
    );

    rpc.register(RegionShared.Cef.Events.GasStation.GasStationHide, async (): Promise<void> => {
      close();
    });

    return () => {
      rpc.unregister(RegionShared.Cef.Events.GasStation.GasStationShow);
      rpc.unregister(RegionShared.Cef.Events.GasStation.GasStationHide);
    };
  }, [isShow, close, dispatch, t, i18n]);

  return {
    isShow,
    close,
  };
};
